(() => {
  'use strict';

  const root = window;
  const document = root.document;
  const RUNTIME_KEY = '__luggexSupportChat';
  if (root[RUNTIME_KEY]) { root[RUNTIME_KEY].scan(); return; }

  const WAIT_MS = 10000;
  const POLL_MS = 150;
  const NATIVE_FALLBACK_MS = 400;
  const MAX_ATTEMPTS = 2;
  let alive = true;
  let pending;
  let timer;
  let sdkState;
  let returnFocus;

  const visible = (element) => {
    if (!element?.isConnected) return false;
    const view = element.ownerDocument?.defaultView || root;
    const styles = view.getComputedStyle(element);
    const bounds = element.getBoundingClientRect();
    if (element.hidden || styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) <= .05 || bounds.width <= 0 || bounds.height <= 0 || bounds.right <= 0 || bounds.bottom <= 0 || bounds.left >= view.innerWidth || bounds.top >= view.innerHeight) return false;
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      const parentStyles = view.getComputedStyle(parent);
      if (parent.hidden || parentStyles.display === 'none' || parentStyles.visibility === 'hidden' || Number(parentStyles.opacity) <= .05) return false;
    }
    return true;
  };
  const renderedChatIsOpen = () => {
    const legacy = document.getElementById('s-chat-plugin');
    if (visible(legacy)) {
      const bounds = legacy.getBoundingClientRect();
      if (bounds.width > 140 && bounds.height > 140) return true;
    }
    return [...document.querySelectorAll('#ss-chat-wrapper [class^="_chat_"]')].some(visible);
  };
  const chatIsOpen = () => !sdkState?.closedSignal && renderedChatIsOpen();
  const sdk = () => root.ssq && typeof root.ssq.push === 'function' ? root.ssq : null;
  const current = (intent) => alive && pending === intent && intent.button.isConnected && intent.card.isConnected;
  const tryNativeLauncher = (intent) => {
    if (!current(intent) || intent.nativeTried || Date.now() < intent.nativeFallbackAt || Date.now() >= intent.deadline || renderedChatIsOpen()) return;
    const frame = document.getElementById('s-chat-plugin');
    if (frame?.tagName !== 'IFRAME' || !visible(frame)) return;
    const bounds = frame.getBoundingClientRect();
    if (bounds.width > 140 || bounds.height > 140) return;
    try {
      // This is the confirmed legacy provider control, not a guessed global,
      // injected SDK, coordinate click, or message-send action. Cross-origin
      // frames remain inaccessible and are never worked around.
      const launcher = frame.contentDocument?.querySelector('[class*="Expand_icon__wrap"]');
      if (!visible(launcher) || typeof launcher.click !== 'function') return;
      intent.nativeTried = true;
      if (sdkState) sdkState.closedSignal = false;
      launcher.click();
      if (current(intent) && chatIsOpen()) finish(intent, true);
    } catch (_) { /* Keep the bounded SDK attempt and email/form fallback. */ }
  };
  const labelFor = (button, card, name, fallback) => button.getAttribute(name) || card.getAttribute(name) || fallback;
  const errorMessageFor = (error) => {
    if (!error) return null;
    const message = error.querySelector('[data-luggex-support-chat-error-message], .luggex-support-chat-card__error-text, [role="status"]');
    if (message) return message;
    if (error.matches('[role="status"], p') && !error.querySelector('a, button')) return error;
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.setAttribute('data-luggex-support-chat-error-message', '');
    error.prepend(status);
    return status;
  };
  const restoreButton = (intent) => {
    if (intent.label) intent.label.textContent = intent.originalLabel;
    if (intent.originalDisabled) intent.button.setAttribute('disabled', '');
    else intent.button.removeAttribute('disabled');
    if (intent.originalBusy === null) intent.button.removeAttribute('aria-busy');
    else intent.button.setAttribute('aria-busy', intent.originalBusy);
    if (intent.originalAriaDisabled === null) intent.button.removeAttribute('aria-disabled');
    else intent.button.setAttribute('aria-disabled', intent.originalAriaDisabled);
  };
  const finish = (intent, success, cancelled = false) => {
    if (pending !== intent) return;
    pending = null;
    root.clearTimeout(timer);
    timer = undefined;
    restoreButton(intent);
    if (success) returnFocus = intent.button;
    else if (!cancelled && intent.error?.isConnected) {
      if (intent.message) intent.message.textContent = labelFor(intent.button, intent.card, 'data-error-label', 'Chat is unavailable right now. Please email us or use the form below.');
      intent.error.hidden = false;
    }
  };
  const queueTick = () => {
    root.clearTimeout(timer);
    if (pending && alive) timer = root.setTimeout(tick, Math.min(POLL_MS, Math.max(0, pending.deadline - Date.now())));
  };
  const bindSdk = (api) => {
    if (sdkState?.api === api) return sdkState;
    const state = { api, readySerial: 0 };
    sdkState = state;
    try {
      api.push('onReady', () => {
        if (!alive || sdkState !== state) return;
        state.readySerial++;
        if (pending) tick();
      });
    } catch (_) { /* A real SDK may still be booting; bounded polling remains available. */ }
    try {
      api.push('onOpenChat', () => {
        if (!alive || sdkState !== state) return;
        state.closedSignal = false;
        if (pending && chatIsOpen()) finish(pending, true);
      });
    } catch (_) {}
    try {
      api.push('onCloseChat', () => {
        if (!alive || sdkState !== state) return;
        state.closedSignal = true;
        if (!returnFocus?.isConnected) return;
        const button = returnFocus;
        returnFocus = null;
        if (!button.disabled) button.focus({ preventScroll: true });
      });
    } catch (_) {}
    return state;
  };
  const attempt = (intent, state) => {
    if (!current(intent) || intent.inFlight || intent.attempts >= MAX_ATTEMPTS || Date.now() >= intent.deadline) return;
    intent.inFlight = true;
    intent.attempts++;
    intent.attemptReadySerial = state.readySerial;
    try {
      state.api.push('chatOpen', (result) => {
        if (!current(intent)) return;
        intent.inFlight = false;
        if (chatIsOpen()) { finish(intent, true); return; }
        if (result?.status === 'success') {
          intent.unsuccessful = false;
          intent.acknowledged = true;
          queueTick();
          return;
        }
        // An early failed/ignored callback can precede SDK readiness. Keep the
        // user's one bounded intent, then allow one retry only on a real Ready
        // signal. Never re-open automatically after this intent has expired.
        if (result?.status === 'failed' || result?.status === 'ignored') {
          intent.unsuccessful = true;
        }
        queueTick();
      });
    } catch (_) {
      intent.inFlight = false;
      intent.unsuccessful = true;
    }
  };
  function tick() {
    root.clearTimeout(timer);
    timer = undefined;
    const intent = pending;
    if (!intent || !alive) return;
    if (!current(intent)) { finish(intent, false, true); return; }
    if (sdkState?.closedSignal && !renderedChatIsOpen()) sdkState.closedSignal = false;
    if (chatIsOpen()) { finish(intent, true); return; }
    if (Date.now() >= intent.deadline) { finish(intent, false); return; }
    const api = sdk();
    if (api) {
      const state = bindSdk(api);
      if (!current(intent)) return;
      if (!intent.attempts || intent.unsuccessful && state.readySerial > intent.attemptReadySerial) attempt(intent, state);
    }
    if (current(intent)) tryNativeLauncher(intent);
    queueTick();
  }
  const requestChat = (button) => {
    if (!alive || pending || !button?.isConnected) return;
    const card = button.closest('[data-luggex-support-chat-card]') || button.parentElement;
    const label = button.querySelector('[data-luggex-support-chat-label]');
    const error = card.querySelector('[data-luggex-support-chat-error]');
    const message = errorMessageFor(error);
    const now = Date.now();
    const intent = { button, card, label, error, message, originalLabel: label?.textContent, originalDisabled: button.hasAttribute('disabled'), originalBusy: button.getAttribute('aria-busy'), originalAriaDisabled: button.getAttribute('aria-disabled'), deadline: now + WAIT_MS, nativeFallbackAt: now + NATIVE_FALLBACK_MS, nativeTried: false, attempts: 0, inFlight: false, unsuccessful: false };
    pending = intent;
    if (error) error.hidden = true;
    if (message) message.textContent = '';
    if (label) label.textContent = labelFor(button, card, 'data-loading-label', 'Opening chat…');
    button.setAttribute('disabled', '');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    // The existing launcher owns its appearance gate. This event expresses a
    // user action without changing platform configuration or creating an SDK.
    document.dispatchEvent(new CustomEvent('luggex:chat-request'));
    const api = sdk();
    if (api) bindSdk(api);
    if (pending === intent) tick();
  };

  const formFor = (link) => link.closest('.shopify-section')?.querySelector('form[id^="ContactForm-"]') || document.querySelector('form[id^="ContactForm-"]');
  const scan = () => {
    if (!alive) return;
    document.querySelectorAll('[data-luggex-support-chat-form-link]').forEach((link) => {
      const form = formFor(link);
      if (form?.id) link.setAttribute('href', `#${encodeURIComponent(form.id)}`);
    });
  };
  const onClick = (event) => {
    const target = event.target?.closest?.('[data-luggex-support-chat-open], [data-luggex-support-chat-form-link]');
    if (!target) return;
    if (target.hasAttribute('data-luggex-support-chat-open')) {
      event.preventDefault();
      requestChat(target);
      return;
    }
    const form = formFor(target);
    if (!form) return;
    event.preventDefault();
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const field = [...form.querySelectorAll('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])')].find((input) => visible(input));
    field?.focus({ preventScroll: true });
  };
  const onUnload = (event) => {
    if (pending && event.target?.contains?.(pending.card)) finish(pending, false, true);
    if (returnFocus && event.target?.contains?.(returnFocus)) returnFocus = null;
  };
  const observer = new MutationObserver((records) => {
    if (pending && !pending.card.isConnected) finish(pending, false, true);
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches('[data-luggex-support-chat-card], form[id^="ContactForm-"]') || node.querySelector('[data-luggex-support-chat-card], form[id^="ContactForm-"]'))))) scan();
  });
  const destroy = () => {
    if (!alive) return;
    if (pending) finish(pending, false, true);
    alive = false;
    returnFocus = null;
    root.clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener('click', onClick);
    document.removeEventListener('shopify:section:load', scan);
    document.removeEventListener('shopify:section:unload', onUnload);
    document.removeEventListener('DOMContentLoaded', scan);
    root.removeEventListener('pagehide', onPageHide);
    root.removeEventListener('pageshow', scan);
    if (root[RUNTIME_KEY]?.destroy === destroy) delete root[RUNTIME_KEY];
  };
  const onPageHide = (event) => {
    if (!event.persisted) { destroy(); return; }
    if (pending) finish(pending, false, true);
    returnFocus = null;
  };
  document.addEventListener('click', onClick);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', onUnload);
  document.addEventListener('DOMContentLoaded', scan, { once: true });
  root.addEventListener('pagehide', onPageHide);
  root.addEventListener('pageshow', scan);
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  scan();
  root[RUNTIME_KEY] = { scan, destroy };
})();
